import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import repositoryContainer from '../../../services/RepositoryContainer';
import { useToast } from '../../../components/context/ToastContext';

const BulkActionsPanel = ({ onBulkAction }) => {
  const [actionType, setActionType] = useState('set-available');
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '17:00' });
  const [recurringPattern, setRecurringPattern] = useState('none');
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const actionOptions = [
    { value: 'set-available', label: 'Set as Available' },
    { value: 'set-unavailable', label: 'Set as Unavailable' },
    { value: 'block-dates', label: 'Block Dates' },
   // { value: 'copy-schedule', label: 'Copy Schedule' }
  ];

  const recurringOptions = [
    { value: 'none', label: 'No Recurrence' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const handleDayToggle = (day) => {
    setSelectedDays(prev => 
      prev?.includes(day) 
        ? prev?.filter(d => d !== day)
        : [...prev, day]
    );
  };


  const handleBulkAction = async () => {
    setIsLoading(true);

    try {
      // Map actionType to API action name (API expects "block" not "block-dates")
      const apiAction = actionType === 'block-dates' ? 'block' : actionType;

      // Use the current month as a reasonable default range for bulk operations
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];

      const daysOfWeekIndices = selectedDays?.map((day) => {
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6
        };
        return dayMap[day.toLowerCase()];
      }).filter((idx) => idx !== undefined);

      await repositoryContainer.dateSlot.bulkUpdate({
        startDate,
        endDate,
        daysOfWeek: daysOfWeekIndices,
        recurringPattern,
        workingHours: {
          startTime: timeRange.start,
          endTime: timeRange.end
        },
        action: apiAction
      });

      // Notify parent that bulk action was applied (if they want to refresh UI)
      onBulkAction?.({
        actionType,
        selectedDays,
        timeRange,
        recurringPattern
      });
      addToast('Bulk action completed successfully!', 'success');

      //('Bulk action completed successfully!');
    } catch (error) {
      console.error('Bulk action failed:', error);
      // alert(error.data?.message || error.message || 'Bulk action failed. Please try again.');
      addToast(error.data?.message || error.message || 'Bulk action failed. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Zap" size={24} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground">
            Bulk Actions
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground font-caption">
            Apply changes to multiple dates at once
          </p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        <Select
          label="Action Type"
          description="Choose what action to perform"
          options={actionOptions}
          value={actionType}
          onChange={setActionType}
        />

        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-3">
            Select Days of Week
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {daysOfWeek?.map((day) => (
              <Checkbox
                key={day?.value}
                label={day?.label}
                checked={selectedDays?.includes(day?.value)}
                onChange={() => handleDayToggle(day?.value)}
              />
            ))}
          </div>
        </div>

        {(actionType === 'set-available' || actionType === 'copy-schedule') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-2">
                Start Time
              </label>
              <input
                type="time"
                value={timeRange?.start}
                onChange={(e) => setTimeRange({ ...timeRange, start: e?.target?.value })}
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-2">
                End Time
              </label>
              <input
                type="time"
                value={timeRange?.end}
                onChange={(e) => setTimeRange({ ...timeRange, end: e?.target?.value })}
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        )}

        <Select
          label="Recurring Pattern"
          description="Apply this schedule repeatedly"
          options={recurringOptions}
          value={recurringPattern}
          onChange={setRecurringPattern}
        />

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-caption text-foreground mb-1">
                <strong>Preview:</strong>
              </p>
              <p className="text-xs md:text-sm text-muted-foreground font-caption">
                {selectedDays?.length === 0 
                  ? 'Select days to see preview'
                  : `This action will be applied to ${selectedDays?.length} day(s) ${recurringPattern !== 'none' ? `with ${recurringPattern} recurrence` : ''}`
                }
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            variant="default"
            iconName="Check"
            iconPosition="left"
            onClick={handleBulkAction}
            disabled={selectedDays?.length === 0 || isLoading}
            fullWidth
            className="sm:flex-1"
          >
            Apply Bulk Action
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setSelectedDays([]);
              setActionType('set-available');
              setRecurringPattern('none');
            }}
            fullWidth
            className="sm:flex-1"
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsPanel;